import React, { useState, useEffect } from 'react';

interface CompanyFormData {
  companyName: string;
  companyUrl: string;
  companyState: string;
  primaryContact: string;
  primaryPhone: string;
  primaryEmail: string;
  address: string;
  allowedIpAddresses?: string; // Comma-separated IP addresses or CIDR blocks
  allowAllIps?: boolean; // Flag to allow 0.0.0.0/0
}

interface CompanyFormProps {
  formData: CompanyFormData;
  mode: 'create' | 'edit';
  onSubmit: (data: CompanyFormData) => Promise<void>;
  onCancel: () => void;
  onChange: (field: keyof CompanyFormData, value: string | boolean) => void;
  loading?: boolean;
  errorMessage?: string;
}

export default function CompanyForm({
  formData,
  mode,
  onSubmit,
  onCancel,
  onChange,
  loading = false,
  errorMessage = '',
}: CompanyFormProps) {
  // Use formData directly instead of internal state
  const form = formData;

  console.log('🔍 CompanyForm rendering - formData:', JSON.stringify(formData, null, 2));

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    onChange(name as keyof CompanyFormData, type === 'checkbox' ? checked : value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <label className='block text-sm font-medium text-slate-300 mb-2'>Company Name *</label>
          <input
            type='text'
            name='companyName'
            value={form.companyName}
            onChange={handleChange}
            className='w-full px-3 py-2 bg-slate-800 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
            placeholder='Enter company name'
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-300 mb-2'>Company URL *</label>
          <input
            type='text'
            name='companyUrl'
            value={form.companyUrl}
            onChange={handleChange}
            className='w-full px-3 py-2 bg-slate-800 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
            placeholder='company.com or https://company.com'
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-300 mb-2'>State *</label>
          <select
            name='companyState'
            value={form.companyState}
            onChange={handleChange}
            className='w-full px-3 py-2 bg-slate-800 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
            aria-label='Select company state'
            required
          >
            <option value=''>Select a state</option>
            <option value='AL'>Alabama</option>
            <option value='AK'>Alaska</option>
            <option value='AZ'>Arizona</option>
            <option value='AR'>Arkansas</option>
            <option value='CA'>California</option>
            <option value='CO'>Colorado</option>
            <option value='CT'>Connecticut</option>
            <option value='DE'>Delaware</option>
            <option value='FL'>Florida</option>
            <option value='GA'>Georgia</option>
            <option value='HI'>Hawaii</option>
            <option value='ID'>Idaho</option>
            <option value='IL'>Illinois</option>
            <option value='IN'>Indiana</option>
            <option value='IA'>Iowa</option>
            <option value='KS'>Kansas</option>
            <option value='KY'>Kentucky</option>
            <option value='LA'>Louisiana</option>
            <option value='ME'>Maine</option>
            <option value='MD'>Maryland</option>
            <option value='MA'>Massachusetts</option>
            <option value='MI'>Michigan</option>
            <option value='MN'>Minnesota</option>
            <option value='MS'>Mississippi</option>
            <option value='MO'>Missouri</option>
            <option value='MT'>Montana</option>
            <option value='NE'>Nebraska</option>
            <option value='NV'>Nevada</option>
            <option value='NH'>New Hampshire</option>
            <option value='NJ'>New Jersey</option>
            <option value='NM'>New Mexico</option>
            <option value='NY'>New York</option>
            <option value='NC'>North Carolina</option>
            <option value='ND'>North Dakota</option>
            <option value='OH'>Ohio</option>
            <option value='OK'>Oklahoma</option>
            <option value='OR'>Oregon</option>
            <option value='PA'>Pennsylvania</option>
            <option value='RI'>Rhode Island</option>
            <option value='SC'>South Carolina</option>
            <option value='SD'>South Dakota</option>
            <option value='TN'>Tennessee</option>
            <option value='TX'>Texas</option>
            <option value='UT'>Utah</option>
            <option value='VT'>Vermont</option>
            <option value='VA'>Virginia</option>
            <option value='WA'>Washington</option>
            <option value='WV'>West Virginia</option>
            <option value='WI'>Wisconsin</option>
            <option value='WY'>Wyoming</option>
          </select>
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-300 mb-2'>Primary Contact</label>
          <input
            type='text'
            name='primaryContact'
            value={form.primaryContact}
            onChange={handleChange}
            className='w-full px-3 py-2 bg-slate-800 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
            placeholder='Contact person name'
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-300 mb-2'>Primary Phone</label>
          <input
            type='tel'
            name='primaryPhone'
            value={form.primaryPhone}
            onChange={handleChange}
            className='w-full px-3 py-2 bg-slate-800 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
            placeholder='(555) 123-4567'
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-300 mb-2'>
            Service Account Email *
          </label>
          <input
            type='email'
            name='primaryEmail'
            value={form.primaryEmail}
            onChange={handleChange}
            className='w-full px-3 py-2 bg-slate-800 border border-white/10 rounded text-slate-100 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none'
            placeholder='sftp@company.com or service@company.com'
            required
          />
        </div>

        <div className='md:col-span-2'>
          <label className='block text-sm font-medium text-slate-300 mb-2'>Address</label>
          <textarea
            name='address'
            value={form.address}
            onChange={handleChange}
            className='w-full px-3 py-2 bg-slate-800 border border-white/10 rounded text-slate-100 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none'
            placeholder='Company address'
            rows={3}
          />
        </div>

        <div className='md:col-span-2'>
          <label className='block text-sm font-medium text-slate-300 mb-2'>
            Allowed IP Addresses (Optional)
          </label>
          <input
            type='text'
            name='allowedIpAddresses'
            value={form.allowedIpAddresses || ''}
            onChange={handleChange}
            className='w-full px-3 py-2 bg-slate-800 border border-white/10 rounded text-slate-100 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none'
            placeholder='203.0.113.0/24, 198.51.100.0/24 (comma-separated)'
          />
          <p className='text-xs text-slate-400 mt-1'>
            IP addresses or CIDR blocks allowed to access CDN and SFTP server. Comma-separated.
            <br />
            Examples: 203.0.113.0/24, 198.51.100.0/24, 192.0.2.0/24
          </p>
          
          {/* Security warning for 0.0.0.0/0 */}
          {(form.allowedIpAddresses?.includes('0.0.0.0/0') || form.allowedIpAddresses === '0.0.0.0/0') && (
            <div className='mt-3 p-3 bg-red-900/20 border border-red-700/50 rounded-lg'>
              <label className='flex items-start gap-2 cursor-pointer'>
                <input
                  type='checkbox'
                  name='allowAllIps'
                  checked={form.allowAllIps || false}
                  onChange={(e) => onChange('allowAllIps', e.target.checked)}
                  className='mt-1'
                />
                <div className='flex-1'>
                  <span className='text-sm font-medium text-red-300 block mb-1'>
                    ⚠️ Security Warning: Allow All IPs (0.0.0.0/0)
                  </span>
                  <p className='text-xs text-red-200'>
                    You are allowing access from all IP addresses. This disables IP whitelisting and allows anyone on the internet to access the CDN and SFTP server. 
                    This action will be logged for security auditing. Only check this box if you understand the security implications.
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className='p-3 bg-red-500/20 border border-red-500/30 rounded text-red-300 text-sm'>
          {errorMessage}
        </div>
      )}

      <div className='flex gap-3 pt-4'>
        <button
          type='button'
          onClick={onCancel}
          className='flex-1 px-4 py-2 border border-white/10 rounded text-slate-200 hover:bg-white/10 transition-colors'
        >
          Cancel
        </button>
        <button
          type='submit'
          disabled={
            loading ||
            !form.companyName ||
            !form.companyUrl ||
            !form.companyState ||
            !form.primaryEmail
          }
          className='flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors'
        >
          {loading
            ? mode === 'create'
              ? 'Creating...'
              : 'Updating...'
            : mode === 'create'
              ? 'Create Company'
              : 'Update Company'}
        </button>
      </div>
    </form>
  );
}
